
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { createAtlasIframeService, AtlasIframeConfig } from '@/services/atlasIframeService';
import { StoreModel } from '@/models/store';
import { setSettingsVersion } from '@/config/atlasSettingsLinks';

@Component
export default class AtlasEmbed extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;

  @Prop({ default: '' })
  public agent!: string;

  @Prop({ default: '' })
  public tab!: string;

  private iframeService = createAtlasIframeService();
  public loading = true;
  public error: string | null = null;
  public iframeSrc = '';
  private tabChangeFromAtlas = false;

  /**
   * Get the base URL for the Atlas frontend
   */
  get atlasBaseUrl(): string {
    return process.env.VUE_APP_ATLAS_FRONTEND_URL || 'http://localhost:3000';
  }

  /**
   * Build the initial iframe URL from the agent prop and current query params.
   * Called once at creation — subsequent query param changes from Atlas
   * only update Warbler's URL bar, not the iframe src.
   */
  private buildIframeSrc(): string {
    let url = this.atlasBaseUrl;
    if (this.agent) {
      url = `${this.atlasBaseUrl}/${this.agent}`;
    }
    // Append tab path segment for setup deep linking (e.g. /setup/variants)
    if (this.tab) {
      url += `/${this.tab}`;
    }
    // Forward query params from Warbler's URL to the iframe for deep linking
    const query = this.$route.query;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (Array.isArray(value)) {
        for (const item of value) {
          if (typeof item === 'string') {
            params.append(key, item);
          }
        }
      } else if (typeof value === 'string') {
        params.set(key, value);
      }
    }
    const qs = params.toString();
    if (qs) {
      url += `?${qs}`;
    }
    return url;
  }

  created(): void {
    this.iframeSrc = this.buildIframeSrc();
  }

  /**
   * Watch for agent changes and navigate
   */
  @Watch('agent')
  onAgentChange(newAgent: string): void {
    if (newAgent) {
      this.iframeService.navigateToAgent(newAgent);
    }
  }

  /**
   * Watch for tab changes (e.g. user clicks a different settings item in the menu)
   * and reload the iframe with the new tab URL.
   * Skip reload when the change originated from Atlas itself (via notifyNavigation).
   */
  @Watch('tab')
  onTabChange(): void {
    if (this.tabChangeFromAtlas) {
      this.tabChangeFromAtlas = false;
      return;
    }
    this.loading = true;
    this.error = null;
    this.iframeSrc = '';
    this.$nextTick(() => {
      this.iframeSrc = this.buildIframeSrc();
    });
  }

  private loadingTimeout: number | null = null;

  /**
   * Initialize the iframe service when mounted
   */
  public mounted(): void {
    const iframe = this.$refs.atlasFrame as HTMLIFrameElement;
    if (iframe) {
      this.iframeService.updateConfig({
        baseUrl: this.atlasBaseUrl,
        onReady: this.onAtlasReady.bind(this),
        onNavigation: this.onAtlasNavigation.bind(this),
        onActionComplete: this.onAtlasActionComplete.bind(this),
        onParentNavigate: this.onAtlasParentNavigate.bind(this),
        onQueryParams: this.onAtlasQueryParams.bind(this),
      });
      this.iframeService.initialize(iframe);

      // Fallback: hide loading after 5 seconds if no READY message received
      this.loadingTimeout = window.setTimeout(() => {
        if (this.loading) {
          console.warn('[AtlasEmbed] No READY message received, hiding loading anyway');
          this.loading = false;
        }
      }, 5000);
    }
  }

  /**
   * Clean up when component is destroyed
   */
  public beforeDestroy(): void {
    this.iframeService.destroy();
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
  }

  /**
   * Handle iframe load event
   */
  public onIframeLoad(): void {
    // Iframe loaded - give a short delay then hide loading as fallback
    setTimeout(() => {
      if (this.loading) {
        this.loading = false;
      }
    }, 1000);
  }

  /**
   * Handle iframe error event
   */
  public onIframeError(): void {
    this.loading = false;
    this.error = 'Failed to load Atlas. Please check your connection and try again.';
  }

  /**
   * Handle Atlas frontend ready message
   */
  private onAtlasReady(): void {
    this.loading = false;
    this.error = null;

    // Send auth token
    this.iframeService.sendAuthToken();
  }

  /**
   * Handle navigation changes from Atlas
   */
  private onAtlasNavigation(route: string): void {
    // Navigate to the requested route in Warbler
    // This handles both Warbler routes (e.g., /buying-table) and
    // Atlas iframe routes (e.g., /assistant/forecasting)
    const targetPath = route.startsWith('/') ? route : `/${route}`;
    if (this.$route.path !== targetPath) {
      // Use replace for tab changes within the same agent to avoid polluting history
      const sameAgentTarget = this.agent && (
        targetPath === `/assistant/${this.agent}` ||
        targetPath.startsWith(`/assistant/${this.agent}/`)
      );
      if (sameAgentTarget) {
        this.tabChangeFromAtlas = true;
        this.$router.replace(targetPath).catch(() => {
          this.tabChangeFromAtlas = false;
        });
      } else {
        this.$router.push(targetPath);
      }
    }
  }

  /**
   * Handle parent navigation requests from Atlas.
   * Navigates Warbler's own router (no /atlas prefix).
   */
  private onAtlasParentNavigate(path: string): void {
    // If navigating to a classic settings page, switch back to classic mode
    // (Atlas iframe has separate localStorage, so Warbler must set its own)
    if (path.startsWith('/settings/') || path === '/settings') {
      setSettingsVersion('classic');
    }
    this.$router.push(path).catch(() => {
      // Ignore navigation duplicates
    });
  }

  /**
   * Handle query parameter updates from Atlas.
   * Uses Vue Router replace so the $route watcher in App.vue fires,
   * which in turn calls Shopify App Bridge History.REPLACE when embedded.
   */
  private onAtlasQueryParams(queryString: string): void {
    // Parse the incoming query string into a scalar object for Vue Router.
    // Last value wins for duplicate keys to preserve the scalar contract
    // expected by downstream consumers (ReportPage, HeaderTitle, etc.).
    const params = new URLSearchParams(queryString || '');
    const query: Record<string, string> = {};
    for (const [key, value] of params.entries()) {
      query[key] = value;
    }

    // Use router.replace to trigger the $route watcher in App.vue,
    // which handles Shopify App Bridge History.REPLACE for embedded mode
    this.$router.replace({ query }).catch(() => {
      // Ignore NavigationDuplicated (same query)
    });
  }

  /**
   * Handle action completion from Atlas
   */
  private onAtlasActionComplete(action: string, result: unknown): void {
    // Emit event for parent components if needed
    this.$emit('atlas-action', { action, result });
  }

  /**
   * Reload the iframe
   */
  public reload(): void {
    this.loading = true;
    this.error = null;

    // Rebuild src from current state and force iframe reload
    this.iframeSrc = '';
    this.$nextTick(() => {
      this.iframeSrc = this.buildIframeSrc();
    });
  }
}
